import { useState, useCallback, useEffect } from 'react';
import { UseInfiniteQueryResult } from '@tanstack/react-query';

interface UseInfiniteDropdownProps<T> {
    initialId?: string;
    onChange?: (id: string | null) => void;
    itemsPerPage?: number;
    useInfiniteQuery: (params: any) => UseInfiniteQueryResult<any, unknown>;
    useItemById: (params: { id: string }) => { data?: { item?: T } };
    queryParameters?: Record<string, any>;
}

interface UseInfiniteDropdownResult<T> {
    selectedId: string | null;
    selectedData: T | undefined;
    handleChange: (value: string) => void;
    setFilter: (value: string) => void;
    queryResult: UseInfiniteQueryResult<any, unknown>;
}

export function useInfiniteDropdown<T>({
    initialId,
    onChange,
    itemsPerPage = 50,
    useInfiniteQuery,
    useItemById,
    queryParameters = {}
}: UseInfiniteDropdownProps<T>): UseInfiniteDropdownResult<T> {
    const [selectedId, setSelectedId] = useState<string>(initialId || '');
    const [filterText, setFilterText] = useState('');

    // Query for filtered items
    const queryResult = useInfiniteQuery({
        take: itemsPerPage,
        name: filterText,
        ...queryParameters
    });

    // First try to find the selected item in the existing data
    const selectedItemFromList = selectedId ? queryResult.data?.pages?.flatMap((page: { items: T[] }) => page.items)?.find((item: T & { id: string }) => item.id === selectedId) : undefined;

    // Only query for selected item if not found in list
    const { data: selectedItemData } = useItemById({ 
        id: selectedId 
    });

    const selectedData = selectedItemFromList || selectedItemData?.item;

    const handleChange = useCallback((value: string) => {
        setSelectedId(value);
        setFilterText(''); // Clear filter when an item is selected
        onChange?.(value || null);
    }, [onChange]);

    useEffect(() => {
        if (initialId !== undefined) {
            setSelectedId(initialId);
        }
    }, [initialId]);

    return {
        selectedId,
        selectedData,
        handleChange,
        setFilter: setFilterText,
        queryResult
    };
};
